import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { axiosInstance } from '../../App';
import { CAPABILITIES, can, isPlatform, isTenantAdmin } from '../../lib/access';
import { Card } from '../ui/card';
import InfoTip from '../InfoTip';

// Old Managers keep their abilities until the switch-over script turns them into ticks.
const hasAbility = (person, key) => (key === 'can_delete' ? person.permissions?.can_delete === true : can(person, key));
const ABILITIES = [...CAPABILITIES, { key: 'can_delete', label: 'Delete knowledge', description: 'Remove knowledge files and entries they can see.' }];

// One place to see and change who has each extra ability, instead of opening people one by one.
const AbilitiesSettings = () => {
  const [people, setPeople] = useState([]);
  const [adding, setAdding] = useState({});
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/users');
      setPeople((data || []).filter((person) => !isTenantAdmin(person) && !isPlatform(person) && person.onboarding_status !== 'pending'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load people');
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const holders = useMemo(() => Object.fromEntries(ABILITIES.map(({ key }) => [key, people.filter((person) => hasAbility(person, key))])), [people]);

  const setAbility = async (person, key, value) => {
    setBusy(`${person.id}-${key}`);
    try {
      await axiosInstance.patch(`/users/${person.id}/permissions`, { ...(person.permissions || {}), [key]: value });
      setPeople((current) => current.map((entry) => (entry.id === person.id ? { ...entry, permissions: { ...(entry.permissions || {}), [key]: value } } : entry)));
      toast.success(`${person.name} ${value ? 'can now' : 'can no longer'} ${ABILITIES.find((item) => item.key === key).label.toLowerCase()}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not change this ability');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-abilities">
      <Card className="p-4 text-sm text-slate-600">Members can always ask questions and use their teams’ knowledge. Give extra abilities here, or per person in Teams &amp; Users. Tenant Admins have every ability.</Card>
      {ABILITIES.map((ability) => {
        const current = holders[ability.key] || [];
        const candidates = people.filter((person) => !hasAbility(person, ability.key));
        return (
          <Card key={ability.key} className="p-5" data-testid={`ability-card-${ability.key}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="flex items-center gap-1.5 text-base font-semibold">{ability.label}<InfoTip label={`About ${ability.label}`}>{ability.description}</InfoTip></h3>
                <p className="text-xs text-slate-500">{current.length} {current.length === 1 ? 'person' : 'people'}</p>
              </div>
              <form className="flex items-center gap-2" onSubmit={(event) => {
                event.preventDefault();
                const person = people.find((entry) => entry.id === adding[ability.key]);
                if (person) { setAbility(person, ability.key, true); setAdding((state) => ({ ...state, [ability.key]: '' })); }
              }}>
                <select value={adding[ability.key] || ''} onChange={(event) => setAdding((state) => ({ ...state, [ability.key]: event.target.value }))} className="h-9 rounded-md border bg-white px-2 text-sm" aria-label={`Give ${ability.label} to`}>
                  <option value="">Choose a person</option>
                  {candidates.map((person) => <option key={person.id} value={person.id}>{person.name}</option>)}
                </select>
                <button type="submit" disabled={Boolean(busy) || !adding[ability.key]} className="qflow-accent-bg flex h-9 items-center gap-1 rounded-md px-3 text-sm font-semibold disabled:opacity-50"><Plus size={14} aria-hidden="true" />Give</button>
              </form>
            </div>
            {current.length ? <ul className="mt-3 flex flex-wrap gap-2">{current.map((person) => (
              <li key={person.id} className="qflow-home-chip flex items-center gap-1.5 py-1 pl-3 pr-1.5 text-sm">
                {person.name}
                <button type="button" disabled={Boolean(busy)} onClick={() => setAbility(person, ability.key, false)} aria-label={`Remove ${ability.label} from ${person.name}`} className="grid h-5 w-5 place-items-center rounded-full text-slate-500 hover:text-rose-600"><X size={13} /></button>
              </li>
            ))}</ul> : <p className="mt-3 text-sm text-slate-500">Nobody yet.</p>}
          </Card>
        );
      })}
    </div>
  );
};

export default AbilitiesSettings;
